import type { ProductDetailItem, ProductListItem, ProductOverpackItem, ProductPackagingInfo, ProductPriceInfo } from "~~/models/products";
import { determinePackaging } from "~~/server/utils/products";

/**
 * Tracks the active overpack/price selection for a single product.
 * Call this directly in `<script setup>` (do not wrap it in an outer `computed`), it stays reactive
 * to `productInput`/`pricesInput`/`overpackIdInput` on its own, so the selection isn't wiped every
 * time prices refresh or the product is refetched (e.g. after a locale switch).
 */
export function useOverpack(
  productInput: MaybeRefOrGetter<ProductListItem | ProductDetailItem | null | undefined>,
  pricesInput: MaybeRefOrGetter<Array<ProductPriceInfo> | null | undefined>,
  overpackIdInput?: MaybeRefOrGetter<number | null | undefined>,
) {
  const product = computed(() => toValue(productInput));
  const prices = computed(() => toValue(pricesInput));

  function resolveDefaultOverpackId(): number {
    return product.value?.overpacks?.find((o: ProductOverpackItem) => o.value === toValue(overpackIdInput))?.value
      ?? product.value?.defaultOverpack?.value
      ?? product.value?.overpacks?.at(0)?.value
      ?? 0;
  }

  const selectedOverpackId = ref<number>(resolveDefaultOverpackId());

  // only reset the selection when the underlying product or overpack actually changes, not on every unrelated prices/product refresh
  watch([() => product.value?.number, () => toValue(overpackIdInput)], () => {
    selectedOverpackId.value = resolveDefaultOverpackId();
  });

  function update(overpackId: number) {
    selectedOverpackId.value = overpackId;
  }

  const activeOverpack = computed<{ selection: ProductOverpackItem | null; packaging: ProductPackagingInfo | null } | null>(() => {
    const overpacks = product.value?.overpacks;

    const selectedOverpack = overpacks?.find((o: ProductOverpackItem) => o.value === selectedOverpackId.value)
      ?? product.value?.defaultOverpack
      ?? overpacks?.at(0)
      ?? null;

    return {
      selection: selectedOverpack,
      packaging: determinePackaging(selectedOverpackId.value, product.value),
    };
  });

  const activePrice = computed<ProductPriceInfo | null>(() => {
    if (!product.value || !prices.value) {
      return null;
    }

    const overpackCode = product.value.isVariableWeight ? "kg" : activeOverpack.value?.selection?.code;
    const productNumber = product.value.number;
    return prices.value?.find((p: ProductPriceInfo) => p.productNumber === productNumber && p.overpackCode === overpackCode) ?? null;
  });

  return {
    activeOverpack: activeOverpack,
    activePrice: activePrice,
    update: update,
  };
}
